import { useEffect } from 'react'
import { useAppStore } from '@/store'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import { apiService } from '@/service/api'
import { useTranslation } from '@/i18n'

interface VersionSelectorProps {
  graphId: string
}

export default function VersionSelector({ graphId }: VersionSelectorProps) {
  const { t } = useTranslation()
  const { versionHistory, setVersionHistory, setCurrentVersion, loadVersion } = useAppStore()

  useEffect(() => {
    const fetchVersionHistory = async () => {
      const history = await apiService.getVersionHistory(graphId)
      setVersionHistory(history)
      // Set the current version to the latest version if available
      if (history.length > 0) {
        const latestVersion = history[0].version
        setCurrentVersion(latestVersion)
      }
    }
    fetchVersionHistory()
  }, [graphId, setVersionHistory, setCurrentVersion])

  const handleVersionChange = async (version: string) => {
    const versionNum = parseInt(version)
    if (versionNum === versionHistory.currentVersion) return
    await loadVersion(versionNum)
  }

  return (
    <div>
      <Select
        value={versionHistory.currentVersion?.toString()}
        onValueChange={handleVersionChange}
        disabled={versionHistory.versions.length <= 1}
      >
        <SelectTrigger className="w-auto">
          <SelectValue>
            {versionHistory.currentVersion
              ? t('graph_editor:version.label', { count: versionHistory.currentVersion, defaultValue: 'Version {{count}}' })
              : t('graph_editor:version.none', { defaultValue: 'No versions available' })}
          </SelectValue>
        </SelectTrigger>
        <SelectContent>
          {versionHistory.versions.map(({ version, created_at }) => (
            <SelectItem key={version} value={version.toString()}>
              {t('graph_editor:version.labelWithDate', { count: version, date: new Date(created_at).toLocaleString(), defaultValue: 'Version {{count}} - {{date}}' })}
            </SelectItem>
          ))}
        </SelectContent>
      </Select>
    </div>
  )
} 